import { useState } from "react";
import { format } from "date-fns";
import { Header } from "@/components/Header";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, CalendarIcon, Users, Store, Clock, CheckCircle, Loader2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const COMPANY_SIZES = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "500+", label: "500+ employees" },
];

const PLATFORMS = [
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "magento", label: "Magento" },
  { value: "custom", label: "Custom / Other" },
  { value: "not_sure", label: "Not sure yet" },
];

const TIME_SLOTS = [
  { value: "morning", label: "Morning (9 AM - 12 PM IST)" },
  { value: "afternoon", label: "Afternoon (12 PM - 4 PM IST)" },
  { value: "evening", label: "Evening (4 PM - 7 PM IST)" },
];

const DEMO_BENEFITS = [
  "See the complete business dashboard",
  "Learn how split payments work",
  "Discuss custom integration needs",
  "Get pricing tailored to your volume",
];

const Demo = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    companySize: "",
    platform: "",
    preferredDate: undefined as Date | undefined,
    preferredTime: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field: string, value: string | Date | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmitting(true);

    try {
      // Save to waitlist for tracking
      await supabase
        .from('waitlist_signups')
        .insert({
          email: formData.email,
          source: 'demo_request',
        });

      // Send notification email
      await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: `Demo Request from ${formData.company}`,
          message: `
DEMO REQUEST

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Company Size: ${formData.companySize || 'Not specified'}
Platform: ${formData.platform || 'Not specified'}
Preferred Date: ${formData.preferredDate ? format(formData.preferredDate, 'PPP') : 'Flexible'}
Preferred Time: ${TIME_SLOTS.find(t => t.value === formData.preferredTime)?.label || 'Flexible'}

Additional Notes:
${formData.message || 'None provided'}
          `.trim(),
        },
      });

      setSubmitted(true);
      toast.success("Demo request submitted! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting demo request:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <SEO 
          title="Demo Requested - Shakatah"
          description="Thank you for requesting a demo."
          url="/demo"
        />
        <Header />
        
        <main className="container py-20">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-4">
              Demo Request Received!
            </h1>
            <h2 className="sr-only">Demo request details</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Thanks {formData.name}! We'll reach out to {formData.email} within 24 hours to schedule your personalized demo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/merchant-login?tab=signup">
                  Sign Up Now
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Book a Demo - Shakatah"
        description="Schedule a personalized demo of Shakatah's group gifting and split payment platform for your e-commerce store."
        url="/demo"
      />
      <Header />
      
      <main>
        <section className="relative py-16 md:py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
              {/* Left - Info */}
              <div className="lg:sticky lg:top-24">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  <CalendarIcon className="w-4 h-4" />
                  Book a Demo
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  See Shakatah in Action
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Get a personalized walkthrough of how group gifting can boost your average order value.
                </p>
                
                {/* Benefits */}
                <div className="space-y-4 mb-8">
                  <h2 className="font-semibold text-foreground">What you'll learn:</h2>
                  {DEMO_BENEFITS.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-card border border-border text-center">
                    <Store className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="text-xl font-bold text-foreground">5 min</div>
                    <div className="text-xs text-muted-foreground">Setup Time</div>
                  </div>
                  <div className="p-4 rounded-lg bg-card border border-border text-center">
                    <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="text-xl font-bold text-foreground">3x</div>
                    <div className="text-xs text-muted-foreground">Higher AOV</div>
                  </div>
                  <div className="p-4 rounded-lg bg-card border border-border text-center">
                    <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="text-xl font-bold text-foreground">30 min</div>
                    <div className="text-xs text-muted-foreground">Demo Call</div>
                  </div>
                </div>
              </div>
              
              {/* Right - Form */}
              <Card>
                <CardHeader>
                  <h2 className="text-2xl font-semibold leading-none tracking-tight">Request Your Demo</h2>
                  <CardDescription>
                    Fill in your details and we'll schedule a call at your convenience.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Your Name *</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          disabled={submitting}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          disabled={submitting}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company">Company / Store Name *</Label>
                      <Input
                        id="company"
                        placeholder="Your Store Name"
                        value={formData.company}
                        onChange={(e) => handleInputChange("company", e.target.value)}
                        disabled={submitting}
                        required
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company Size</Label>
                        <Select 
                          value={formData.companySize} 
                          onValueChange={(v) => handleInputChange("companySize", v)}
                          disabled={submitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPANY_SIZES.map((size) => (
                              <SelectItem key={size.value} value={size.value}>
                                {size.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>E-commerce Platform</Label>
                        <Select 
                          value={formData.platform} 
                          onValueChange={(v) => handleInputChange("platform", v)}
                          disabled={submitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select platform" />
                          </SelectTrigger>
                          <SelectContent>
                            {PLATFORMS.map((platform) => (
                              <SelectItem key={platform.value} value={platform.value}>
                                {platform.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Preferred Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !formData.preferredDate && "text-muted-foreground"
                              )}
                              disabled={submitting}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.preferredDate ? format(formData.preferredDate, "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.preferredDate}
                              onSelect={(date) => handleInputChange("preferredDate", date)}
                              disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                              initialFocus
                              className={cn("p-3 pointer-events-auto")}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Time</Label>
                        <Select 
                          value={formData.preferredTime} 
                          onValueChange={(v) => handleInputChange("preferredTime", v)}
                          disabled={submitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_SLOTS.map((slot) => (
                              <SelectItem key={slot.value} value={slot.value}>
                                {slot.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Anything specific you'd like to discuss?</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us about your use case, questions, or specific requirements..."
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        disabled={submitting}
                        rows={3}
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          Request Demo
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      We'll respond within 24 hours. No spam, ever.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
      
      <footer className="border-t border-border bg-card">
        <div className="container py-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              <span className="font-bold text-foreground">Shakatah</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Demo;
